import React, { useMemo, useState, useEffect, useRef } from 'react';
import relationshipOptions from '../relationshipOptions';
import { useHomePersonId } from '../utils/useHomePersonId';
import DuplicateSuggestions, { DuplicateCandidate } from './DuplicateSuggestions';

interface Individual {
  id?: string;
  first_name?: string;
  middle_names?: string;
  surname?: string;
  gender?: string;
  dob?: string;
  status?: string;
  death_dob?: string;
  is_progenitor?: boolean;
  progenitor_line_name?: string;
  is_ghost?: boolean;
  shared_dna_cm?: number | string;
  longest_segment_cm?: number | string;
  number_of_segments?: number | string;
  percent_shared_dna?: number | string;
  predicted_relationship?: string;
  actual_relationship?: string;
  has_dna_match?: boolean;
  is_dna_match?: boolean;  // from API get_individual_full
  dna_source?: string;
  dna_link?: string;
  dna_data?: string;
  confidence_score?: number;
  dna_painter_match_name?: string;
}

interface Relationship {
  id: string;
  from_id: string;
  to_id: string;
  type: string;
  sources?: string[];
}

interface IndividualModalProps {
  open: boolean;
  mode: 'add' | 'edit';
  individual: Individual;
  allIndividuals: Individual[];
  allRelationships: Relationship[];
  onSave: (
    ind: Individual,
    bioFatherId: string | null,
    bioMotherId: string | null,
    spouseId: string | null,
    adoptiveFatherId: string | null,
    adoptiveMotherId: string | null,
  ) => void;
  onCancel: () => void;
  loading?: boolean;
  duplicateError?: string | null;
  onDelete?: (id: string) => void;
  // Possible duplicates: from a 409 when adding, looked up by the modal when editing
  duplicateCandidates?: DuplicateCandidate[];
  onCreateAnyway?: () => void;
  onOpenExisting?: (id: string) => void;
  onMerged?: (keptId: string) => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Utility to parse DNA segment data from MyHeritage or FTDNA
function parseDnaSegments(data: string, source: string) {
  if (!data) return [];
  const lines = data.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const rows = lines.slice(1);
  // FTDNA: Match Name,Chromosome,Start Location,End Location,Centimorgans,Matching SNPs
  // MyHeritage: use existing logic (assume similar columns, but map as needed)
  if (source === 'FTDNA') {
    return rows.map(line => {
      const [match_name, chromosome, start_location, end_location, centimorgans, snps] = line.split(',').map(s => s.trim());
      return {
        match_name,
        chromosome,
        start_location: Number(start_location),
        end_location: Number(end_location),
        centimorgans: Number(centimorgans),
        snps: Number(snps),
      };
    });
  } else if (source === 'MyHeritage') {
    // Assume columns: Match Name,Chromosome,Start Location,End Location,Centimorgans,Matching SNPs (same as FTDNA for now)
    return rows.map(line => {
      const [match_name, chromosome, start_location, end_location, centimorgans, snps] = line.split(',').map(s => s.trim());
      return {
        match_name,
        chromosome,
        start_location: Number(start_location),
        end_location: Number(end_location),
        centimorgans: Number(centimorgans),
        snps: Number(snps),
      };
    });
  }
  // Add more sources as needed
  return [];
}

// Add a helper for chromosome color
function getChromColor(chr: string | number) {
  const palette = ['#43a047','#fbc02d','#0288d1','#8e24aa','#ff7043','#1b5e20','#e91e63','#2196f3','#bdbdbd','#455a64','#ffb300','#6d4c41','#00bcd4','#c62828','#ad1457','#2e7d32','#f57c00','#5d4037','#1976d2','#c0ca33','#7b1fa2','#0097a7','#d84315','#388e3c','#f06292','#afb42b','#00838f','#d32f2f','#7e57c2','#689f38','#ffa000','#303f9f','#cddc39','#ff5722','#607d8b','#795548','#00acc1','#f44336','#8bc34a','#ff9800','#3f51b5','#9c27b0','#4caf50','#e65100','#1a237e','#e91e63','#00e676','#ff1744','#00bfae','#d500f9','#ffea00','#00c853','#ff6d00','#aeea00','#6200ea','#ffd600','#00b8d4','#ffab00','#304ffe','#c51162','#64dd17','#ff3d00','#0091ea','#d4e157'];
  const idx = (typeof chr === 'string' ? parseInt(chr, 10) : chr) % palette.length;
  return palette[idx] || '#888';
}

const IndividualModal: React.FC<IndividualModalProps> = ({
  open,
  mode,
  individual,
  allIndividuals,
  allRelationships,
  onSave,
  onCancel,
  loading,
  duplicateError,
  onDelete,
  duplicateCandidates,
  onCreateAnyway,
  onOpenExisting,
  onMerged,
}) => {
  const homePersonId = useHomePersonId();
  const [form, setForm] = useState<Individual>(individual);
  const [bioFatherId, setBioFatherId] = useState<string | null>(null);
  const [bioMotherId, setBioMotherId] = useState<string | null>(null);
  const [adoptiveFatherId, setAdoptiveFatherId] = useState<string | null>(null);
  const [adoptiveMotherId, setAdoptiveMotherId] = useState<string | null>(null);
  const [spouseId, setSpouseId] = useState<string | null>(null);

  // Potential parent/child state
  const [potentialParents, setPotentialParents] = useState<any[]>([]);
  const [potentialChildren, setPotentialChildren] = useState<any[]>([]);
  const [addPotentialParentId, setAddPotentialParentId] = useState('');
  const [addPotentialChildId, setAddPotentialChildId] = useState('');
  const [potentialLoading, setPotentialLoading] = useState(false);

  // Add hasDnaMatch state, defaulting from form.has_dna_match or false
  const [hasDnaMatch, setHasDnaMatch] = useState(!!form.has_dna_match);
  const [confidenceScore, setConfidenceScore] = useState(typeof form.confidence_score === 'number' ? form.confidence_score : 0.5);
  const [painterMatchName, setPainterMatchName] = useState<string>('');
  const [painterMatches, setPainterMatches] = useState<any[]>([]);
  // Guard against the full-individual-fetch useEffect overwriting hasDnaMatch
  // after the user has already interacted with the DNA Painter match dropdown.
  const userSetDnaMatch = useRef(false);

  const dnaSources: string[] = [
    'Ancestry',
    'FTDNA',
    'MyHeritage',
    'GEDMATCH',
    '23andme',
  ];

  // Deduplicate individuals by ID for dropdowns
  const uniqueIndividuals = useMemo(() => Array.from(new Map(allIndividuals.map(i => [i.id, i])).values()), [allIndividuals]);
  // Bio Father / Bio Mother refer to biological parentage (paternal/maternal line),
  // not to a stored gender field — so all individuals are selectable for either role.
  const fatherOptions = uniqueIndividuals;
  const motherOptions = uniqueIndividuals;
  // Filter out already-added potential parents from dropdown
  const availablePotentialParents = uniqueIndividuals.filter(i => i.id !== individual.id && !potentialParents.some((p: any) => p.individual.id === i.id));
  const availablePotentialChildren = uniqueIndividuals.filter(i => i.id !== individual.id && !potentialChildren.some((c: any) => c.individual.id === i.id));

  // Set initial parent IDs if editing
  useEffect(() => {
    userSetDnaMatch.current = false;   // reset on each new individual
    setForm(individual);
    setHasDnaMatch(!!individual.has_dna_match);
    setConfidenceScore(typeof individual.confidence_score === 'number' ? individual.confidence_score : 0.5);
    if (mode === 'edit' && individual.id) {
      const byTypeSorted = (t: string) =>
        allRelationships
          .filter(r => r.to_id === individual.id && r.type === t)
          .sort((a, b) => a.id.localeCompare(b.id));
      setBioFatherId(byTypeSorted('Bio Father')[0]?.from_id || '');
      setBioMotherId(byTypeSorted('Bio Mother')[0]?.from_id || '');
      setAdoptiveFatherId(byTypeSorted('Adoptive Father')[0]?.from_id || '');
      setAdoptiveMotherId(byTypeSorted('Adoptive Mother')[0]?.from_id || '');
      // Find spouse/partner relationship
      const spouseRel = allRelationships.find(r => (r.from_id === individual.id || r.to_id === individual.id) && r.type === 'Spouse');
      setSpouseId(spouseRel ? (spouseRel.from_id === individual.id ? spouseRel.to_id : spouseRel.from_id) : '');
    } else {
      setBioFatherId('');
      setBioMotherId('');
      setAdoptiveFatherId('');
      setAdoptiveMotherId('');
      setSpouseId('');
    }
    setPainterMatchName(individual.dna_painter_match_name || '');
  }, [individual, mode, allRelationships]);

  useEffect(() => {
    if (!open) return;
    const q = encodeURIComponent(homePersonId);
    fetch(`${API_URL}/dna_painter/matches?home_id=${q}`)
      .then(r => r.json())
      .then(data => setPainterMatches(Array.isArray(data) ? data : []))
      .catch(() => setPainterMatches([]));
  }, [open, homePersonId]);

  // In edit mode, fetch full individual to confirm has_dna_match from the server.
  // Guard: don't overwrite hasDnaMatch if the user has already interacted with
  // the DNA Painter match dropdown (avoids a race where the fetch completes after
  // the user's onChange and resets the checkbox to false).
  useEffect(() => {
    if (!open || mode !== 'edit' || !individual.id) return;
    fetch(`${API_URL}/individuals/${individual.id}/full`)
      .then(r => r.json())
      .then(data => {
        if (userSetDnaMatch.current) return; // user already made a choice — don't override
        const isMatch = !!(data?.individual?.is_dna_match || data?.individual?.has_dna_match);
        setHasDnaMatch(isMatch);
      })
      .catch(() => {});
  }, [open, mode, individual.id]);

  // Fetch potential parents/children
  useEffect(() => {
    if (!individual.id) return;
    setPotentialLoading(true);
    Promise.all([
      fetch(`${API_URL}/individuals/${individual.id}/potential_parents`).then(r => r.json()),
      fetch(`${API_URL}/individuals/${individual.id}/potential_children`).then(r => r.json()),
    ]).then(([parents, children]) => {
      setPotentialParents(parents);
      setPotentialChildren(children);
      setPotentialLoading(false);
    }).catch(() => setPotentialLoading(false));
  }, [individual.id, open]);

  // Find children
  const children = useMemo(() => {
    if (!individual.id) return [];
    return allRelationships
      .filter(r =>
        r.from_id === individual.id &&
        ['Bio Father', 'Bio Mother', 'Adoptive Father', 'Adoptive Mother'].includes(r.type),
      )
      .map(r => allIndividuals.find(i => i.id === r.to_id))
      .filter(Boolean) as Individual[];
  }, [individual.id, allRelationships, allIndividuals]);

  const handleChange = (field: keyof Individual, value: any) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    // Sync DNA Painter link
    if (individual.id && painterMatchName !== (individual.dna_painter_match_name || '')) {
      if (painterMatchName) {
        await fetch(`${API_URL}/dna_painter/link`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ home_id: homePersonId, match_name: painterMatchName, individual_id: individual.id }),
        }).catch(() => {});
      } else if (individual.dna_painter_match_name) {
        await fetch(`${API_URL}/dna_painter/link`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ home_id: homePersonId, match_name: individual.dna_painter_match_name, individual_id: null }),
        }).catch(() => {});
      }
    }
    onSave({
      ...form,
      id: individual.id,
      has_dna_match: hasDnaMatch,
      is_ghost: !!form.is_ghost,
      confidence_score: form.is_ghost ? confidenceScore : undefined,
      dna_painter_match_name: painterMatchName || undefined,
    }, bioFatherId || null, bioMotherId || null, spouseId || null, adoptiveFatherId || null, adoptiveMotherId || null);
  };

  // Add/remove potential parent/child
  const handleAddPotentialParent = async () => {
    if (!addPotentialParentId || !individual.id) return;
    // Prevent duplicate potential parents
    if (potentialParents.some((p: any) => p.individual.id === addPotentialParentId)) {
      setAddPotentialParentId('');
      return;
    }
    setPotentialLoading(true);
    await fetch(`${API_URL}/potential_parent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_id: addPotentialParentId, child_id: individual.id }),
    });
    setAddPotentialParentId('');
    // Refresh
    const parents = await fetch(`${API_URL}/individuals/${individual.id}/potential_parents`).then(r => r.json());
    setPotentialParents(parents);
    setPotentialLoading(false);
  };
  const handleRemovePotentialParent = async (parentId: string) => {
    if (!parentId || !individual.id) return;
    setPotentialLoading(true);
    await fetch(`${API_URL}/potential_parent`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_id: parentId, child_id: individual.id }),
    });
    // Refresh
    const parents = await fetch(`${API_URL}/individuals/${individual.id}/potential_parents`).then(r => r.json());
    setPotentialParents(parents);
    setPotentialLoading(false);
  };
  const handleAddPotentialChild = async () => {
    if (!addPotentialChildId || !individual.id) return;
    setPotentialLoading(true);
    await fetch(`${API_URL}/potential_parent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_id: individual.id, child_id: addPotentialChildId }),
    });
    setAddPotentialChildId('');
    // Refresh
    const children = await fetch(`${API_URL}/individuals/${individual.id}/potential_children`).then(r => r.json());
    setPotentialChildren(children);
    setPotentialLoading(false);
  };
  const handleRemovePotentialChild = async (childId: string) => {
    if (!childId || !individual.id) return;
    setPotentialLoading(true);
    await fetch(`${API_URL}/potential_parent`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_id: individual.id, child_id: childId }),
    });
    // Refresh
    const children = await fetch(`${API_URL}/individuals/${individual.id}/potential_children`).then(r => r.json());
    setPotentialChildren(children);
    setPotentialLoading(false);
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <form className="modal-content genealogy-card" style={{ maxWidth: 500, position: 'relative' }} onSubmit={handleSave}>
        <h3 className="heading-3" style={{ color: 'var(--color-text-inverse)' }}>{mode === 'add' ? 'Add Individual' : 'Edit Individual'}</h3>
        {duplicateError && (
          <div style={{ color: 'var(--color-error)', marginBottom: 12, fontWeight: 600, fontSize: '1.05em' }}>{duplicateError}</div>
        )}
        <DuplicateSuggestions
          mode={mode}
          candidates={duplicateCandidates}
          onCreateAnyway={onCreateAnyway}
          onOpenExisting={onOpenExisting}
          individualId={mode === 'edit' ? individual.id : undefined}
          onMerged={onMerged}
        />
        <input type="text" placeholder="First Name" value={form.first_name || ''} onChange={e => handleChange('first_name', e.target.value)} className="add-person-modal-input" style={{ marginBottom: 8 }} />
        <input type="text" placeholder="Middle Names" value={form.middle_names || ''} onChange={e => handleChange('middle_names', e.target.value)} className="add-person-modal-input" style={{ marginBottom: 8 }} />
        <input type="text" placeholder="Surname" value={form.surname || ''} onChange={e => handleChange('surname', e.target.value)} className="add-person-modal-input" style={{ marginBottom: 8 }} />
        <select value={form.gender || 'unknown'} onChange={e => handleChange('gender', e.target.value)} className="add-person-modal-input" style={{ marginBottom: 8 }}>
          <option value="unknown">Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
        <input type="text" placeholder="Date of Birth (DD/MM/YYYY)" value={form.dob || ''} onChange={e => handleChange('dob', e.target.value)} className="add-person-modal-input" style={{ marginBottom: 8 }} />
        <select value={form.status || 'Living'} onChange={e => handleChange('status', e.target.value)} className="add-person-modal-input" style={{ marginBottom: 8 }}>
          <option value="Living">Living</option>
          <option value="Deceased">Deceased</option>
          <option value="Unknown">Unknown</option>
        </select>
        {form.status === 'Deceased' && (
          <input type="text" placeholder="Date of Death (DD/MM/YYYY)" value={form.death_dob || ''} onChange={e => handleChange('death_dob', e.target.value)} className="add-person-modal-input" style={{ marginBottom: 8 }} />
        )}
        <label style={{ display: 'block', marginBottom: 8 }}>
          <input type="checkbox" checked={!!form.is_progenitor} onChange={e => handleChange('is_progenitor', e.target.checked)} />{' '}Progenitor
        </label>
        {form.is_progenitor && (
          <input type="text" placeholder="Progenitor Line Name" value={form.progenitor_line_name || ''} onChange={e => handleChange('progenitor_line_name', e.target.value)} className="add-person-modal-input" style={{ marginBottom: 8 }} />
        )}
        <label className="add-person-modal-label">
          <input
            type="checkbox"
            checked={!!form.is_ghost}
            onChange={e => handleChange('is_ghost', e.target.checked)}
          />
          <span style={{ marginLeft: 8 }}>Ghost (hypothetical ancestor)</span>
        </label>
        {form.is_ghost && (
          <div style={{ margin: '12px 0 0 0' }}>
            <label htmlFor="confidence-score-slider" style={{ fontWeight: 500, color: '#888', marginBottom: 4, display: 'block' }}>
              Confidence Score: <span style={{ color: '#43a047', fontWeight: 600 }}>{(confidenceScore * 100).toFixed(0)}%</span>
            </label>
            <input
              id="confidence-score-slider"
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={confidenceScore}
              onChange={e => setConfidenceScore(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {/* Bio Father and Bio Mother dropdowns */}
        <label style={{ display: 'block', marginBottom: 8 }}>Bio Father
          <select className="add-person-modal-input" value={bioFatherId || ''} onChange={e => setBioFatherId(e.target.value)}>
            <option value="">None</option>
            {fatherOptions.map(i => (
              <option key={i.id} value={i.id}>{[i.first_name, i.middle_names, i.surname].filter(Boolean).join(' ')}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'block', marginBottom: 8 }}>Bio Mother
          <select className="add-person-modal-input" value={bioMotherId || ''} onChange={e => setBioMotherId(e.target.value)}>
            <option value="">None</option>
            {motherOptions.map(i => (
              <option key={i.id} value={i.id}>{[i.first_name, i.middle_names, i.surname].filter(Boolean).join(' ')}</option>
            ))}
          </select>
        </label>
        <p style={{ margin: '0 0 8px', fontSize: 12, opacity: 0.85 }}>
          DNA paths and MRCA use only Bio Father / Bio Mother. Use adoptive fields for legal or social parents.
        </p>
        <label style={{ display: 'block', marginBottom: 8 }}>Adoptive Father
          <select className="add-person-modal-input" value={adoptiveFatherId || ''} onChange={e => setAdoptiveFatherId(e.target.value)}>
            <option value="">None</option>
            {fatherOptions.map(i => (
              <option key={i.id} value={i.id}>{[i.first_name, i.middle_names, i.surname].filter(Boolean).join(' ')}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'block', marginBottom: 8 }}>Adoptive Mother
          <select className="add-person-modal-input" value={adoptiveMotherId || ''} onChange={e => setAdoptiveMotherId(e.target.value)}>
            <option value="">None</option>
            {motherOptions.map(i => (
              <option key={i.id} value={i.id}>{[i.first_name, i.middle_names, i.surname].filter(Boolean).join(' ')}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'block', marginBottom: 8 }}>Spouse/Partner
          <select className="add-person-modal-input" value={spouseId || ''} onChange={e => setSpouseId(e.target.value)}>
            <option value="">None</option>
            {uniqueIndividuals.filter(i => i.id !== individual.id).map(i => (
              <option key={i.id} value={i.id}>{[i.first_name, i.middle_names, i.surname].filter(Boolean).join(' ')}</option>
            ))}
          </select>
        </label>
        {/* DNA Painter Match Link */}
        {mode === 'edit' && (
          <label style={{ display: 'block', marginBottom: 8 }}>DNA Painter Match
            <select
              className="add-person-modal-input"
              value={painterMatchName}
              onChange={async e => {
                const name = e.target.value;
                setPainterMatchName(name);
                userSetDnaMatch.current = true; // guard against background fetch overwriting
                if (name) {
                  const match = painterMatches.find((m: any) => m.match_name === name);
                  if (match) {
                    // Map DNA Painter source names to the app's dna_source options.
                    // Sources without a segment-data textarea (Ancestry, 23andMe) are
                    // mapped to FTDNA so the textarea + preview appear — the data format
                    // is identical to FTDNA segment export.
                    const srcMap: { [k: string]: string } = {
                      'ancestrydna': 'FTDNA', 'ancestry': 'FTDNA',
                      'familytreedna': 'FTDNA', 'ftdna': 'FTDNA',
                      '23andme': 'FTDNA',
                      'myheritage': 'MyHeritage',
                      'gedmatch': 'GEDMATCH',
                    };
                    const mappedSrc = match.match_source
                      ? srcMap[match.match_source.toLowerCase()] || 'FTDNA'
                      : 'FTDNA';

                    // Fetch the individual segments for this match and build
                    // an FTDNA-format CSV so the live segment preview appears.
                    let dnaData = '';
                    try {
                      const segs: any[] = await fetch(
                        `${API_URL}/dna_painter/segments?home_id=${encodeURIComponent(homePersonId)}`
                      ).then(r => r.json());
                      const matchSegs = segs.filter((s: any) => s.match_name === name);
                      if (matchSegs.length > 0) {
                        const header = 'Match Name,Chromosome,Start Location,End Location,Centimorgans,Matching SNPs';
                        const rows = matchSegs.map((s: any) =>
                          `${s.match_name},${s.chromosome},${s.start},${s.end},${s.cm},${s.snps}`
                        );
                        dnaData = [header, ...rows].join('\n');
                      }
                    } catch { /* silently skip — user can paste manually */ }

                    setHasDnaMatch(true);
                    setForm(f => ({
                      ...f,
                      shared_dna_cm: match.total_cm,
                      number_of_segments: match.num_segments,
                      dna_source: mappedSrc,
                      ...(dnaData ? { dna_data: dnaData } : {}),
                    }));
                  }
                }
              }}
            >
              <option value="">None</option>
              {painterMatches
                .filter((m: any) => !m.linked_individual_id || m.linked_individual_id === individual.id)
                .map((m: any) => (
                  <option key={m.match_name} value={m.match_name}>
                    {m.match_name}{m.match_source ? ` (${m.match_source})` : ''} — {m.total_cm} cM, {m.num_segments} segs
                  </option>
                ))}
            </select>
          </label>
        )}
        {/* DNA/relationship fields (as before) */}
        <label className="add-person-dna-match-checkbox" style={{ display: 'block', marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={hasDnaMatch}
            onChange={e => {
              setHasDnaMatch(e.target.checked);
              setForm(f => ({ ...f, has_dna_match: e.target.checked }));
            }}
          />{' '}
          DNA Match
        </label>
        {hasDnaMatch && (
          <div className="add-person-dna-section">
            <label className="add-person-dna-source-label">DNA Source
              <select
                className="add-person-dna-source-dropdown add-person-modal-input"
                value={form.dna_source || ''}
                onChange={e => handleChange('dna_source', e.target.value)}
              >
                <option value="">Select Source</option>
                {dnaSources.map((src: string) => (
                  <option key={src} value={src}>{src}</option>
                ))}
              </select>
            </label>
            <label className="add-person-dna-link-label">Link (URL)
              <input
                type="url"
                className="add-person-dna-link-input add-person-modal-input"
                placeholder="Paste DNA profile link here"
                value={form.dna_link || ''}
                onChange={e => handleChange('dna_link', e.target.value)}
              />
            </label>
            <label className="add-person-dna-shared-label">Shared DNA (cM)
              <input
                type="number"
                className="add-person-dna-shared-input add-person-modal-input"
                placeholder="Shared DNA (cM)"
                value={form.shared_dna_cm || ''}
                onChange={e => handleChange('shared_dna_cm', e.target.value)}
              />
            </label>
            <label className="add-person-dna-longest-label">Longest Segment (cM)
              <input
                type="number"
                className="add-person-dna-longest-input add-person-modal-input"
                placeholder="Longest Segment (cM)"
                value={form.longest_segment_cm || ''}
                onChange={e => handleChange('longest_segment_cm', e.target.value)}
              />
            </label>
            <label className="add-person-dna-segments-label">Number of Segments
              <input
                type="number"
                className="add-person-dna-segments-input add-person-modal-input"
                placeholder="Number of Segments"
                value={form.number_of_segments || ''}
                onChange={e => handleChange('number_of_segments', e.target.value)}
              />
            </label>
            <label className="add-person-dna-percent-label">% Shared DNA
              <input
                type="number"
                className="add-person-dna-percent-input add-person-modal-input"
                placeholder="% Shared DNA"
                value={form.percent_shared_dna || ''}
                onChange={e => handleChange('percent_shared_dna', e.target.value)}
              />
            </label>
            <label className="add-person-dna-predicted-label">Predicted Relationship
              <select
                className="add-person-dna-predicted-dropdown add-person-modal-input"
                value={form.predicted_relationship || ''}
                onChange={e => handleChange('predicted_relationship', e.target.value)}
              >
                <option value="">Select Predicted Relationship</option>
                {relationshipOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <label className="add-person-dna-actual-label">Actual Relationship
              <select
                className="add-person-dna-actual-dropdown add-person-modal-input"
                value={form.actual_relationship || ''}
                onChange={e => handleChange('actual_relationship', e.target.value)}
              >
                <option value="">Select Actual Relationship</option>
                {relationshipOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            {(form.dna_source === 'FTDNA' || form.dna_source === 'MyHeritage' || form.dna_source === 'GEDMATCH' || !!form.dna_data) && (
              <label className="add-person-dna-data-label">Paste DNA Data
                <textarea
                  className="add-person-dna-data-textarea add-person-modal-input"
                  placeholder="Paste DNA data here for processing"
                  value={form.dna_data || ''}
                  onChange={e => handleChange('dna_data', e.target.value)}
                  style={{ minHeight: 80 }}
                />
                {/* Live preview of parsed segments */}
                {form.dna_data && form.dna_source && parseDnaSegments(form.dna_data, form.dna_source).length > 0 && (
                  <div style={{ marginTop: 18, background: '#f7fafc', borderRadius: 8, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: '#23272f' }}>Preview Parsed Segments</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '0.98em', background: 'white', borderRadius: 6 }}>
                        <thead>
                          <tr>
                            <th>Match Name</th>
                            <th>Chr</th>
                            <th>Start</th>
                            <th>End</th>
                            <th>cM</th>
                            <th>SNPs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseDnaSegments(form.dna_data, form.dna_source).map((seg, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? '#f7fafc' : '#fff' }}>
                              <td>{seg.match_name}</td>
                              <td style={{ color: getChromColor(seg.chromosome) }}>{seg.chromosome}</td>
                              <td>{seg.start_location}</td>
                              <td>{seg.end_location}</td>
                              <td>{seg.centimorgans}</td>
                              <td>{seg.snps}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </label>
            )}
          </div>
        )}
        {/* Children display */}
        {mode === 'edit' && children.length > 0 && (
          <div style={{ margin: '16px 0' }}>
            <strong>Children:</strong>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {children.map(child => (
                <li key={child.id}>{[child.first_name, child.middle_names, child.surname].filter(Boolean).join(' ')}</li>
              ))}
            </ul>
          </div>
        )}
        {/* Potential Relationships */}
        {mode === 'edit' && (
          <div style={{ margin: '24px 0 0 0', borderTop: '1px solid #eee', paddingTop: 16 }}>
            <strong>Potential Parents</strong>
            {potentialLoading ? <div>Loading...</div> : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {potentialParents.map((p: any) => (
                  <li key={p.individual.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {[p.individual.first_name, p.individual.middle_names, p.individual.surname].filter(Boolean).join(' ') || 'Unknown'}
                    <button type="button" className="genealogy-btn-secondary" style={{ fontSize: 12, padding: '2px 8px' }} onClick={() => handleRemovePotentialParent(p.individual.id)}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <select className="add-person-modal-input" value={addPotentialParentId} onChange={e => setAddPotentialParentId(e.target.value)}>
                <option value="">Add Potential Parent</option>
                {availablePotentialParents.map(i => (
                  <option key={i.id} value={i.id}>{[i.first_name, i.middle_names, i.surname].filter(Boolean).join(' ') || 'Unknown'}</option>
                ))}
              </select>
              <button type="button" className="genealogy-btn-primary" style={{ fontSize: 13, padding: '4px 12px' }} onClick={handleAddPotentialParent} disabled={!addPotentialParentId || potentialParents.some((p: any) => p.individual.id === addPotentialParentId)}>Add</button>
            </div>
            <div style={{ marginTop: 18 }}>
              <strong>Potential Children</strong>
              {potentialLoading ? <div>Loading...</div> : (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {potentialChildren.map((c: any) => (
                    <li key={c.individual.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {[c.individual.first_name, c.individual.middle_names, c.individual.surname].filter(Boolean).join(' ') || 'Unknown'}
                      <button type="button" className="genealogy-btn-secondary" style={{ fontSize: 12, padding: '2px 8px' }} onClick={() => handleRemovePotentialChild(c.individual.id)}>Remove</button>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <select className="add-person-modal-input" value={addPotentialChildId} onChange={e => setAddPotentialChildId(e.target.value)}>
                  <option value="">Add Potential Child</option>
                  {availablePotentialChildren.map(i => (
                    <option key={i.id} value={i.id}>{[i.first_name, i.middle_names, i.surname].filter(Boolean).join(' ') || 'Unknown'}</option>
                  ))}
                </select>
                <button type="button" className="genealogy-btn-primary" style={{ fontSize: 13, padding: '4px 12px' }} onClick={handleAddPotentialChild} disabled={!addPotentialChildId}>Add</button>
              </div>
            </div>
          </div>
        )}
        <div className="individual-modal-actions">
          <button className="genealogy-btn-primary" type="submit" disabled={loading}>{mode === 'add' ? 'Add Individual' : 'Save'}</button>
          <button className="genealogy-btn-secondary" type="button" onClick={onCancel}>Cancel</button>
          {onDelete && mode === 'edit' && individual.id && (
            <button
              type="button"
              style={{ background: 'var(--color-error)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: '1em', cursor: 'pointer', marginLeft: 8 }}
              onClick={() => onDelete(individual.id as string)}
            >
              Delete
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default IndividualModal; 